import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Film, Star, MapPin, Clock, Utensils, Loader2, Ticket } from "lucide-react";
import { api, apiError } from "@/lib/api";

const SEATS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export default function Cinema() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null); // {movie, cinema, time}
  const [seat, setSeat] = useState("");
  const [rests, setRests] = useState(null);

  useEffect(() => { api.get("/cinema/shows").then((r) => setData(r.data)).catch(() => setData({ movies: [] })); }, []);

  const chooseTime = (movie, cinema, time) => { setSel({ movie, cinema, time }); setSeat(""); setRests(null); };

  const confirmSeat = async () => {
    if (!seat) return toast.error("Pick your seat");
    try { const { data } = await api.get(`/cinema/${sel.cinema.id}/restaurants`); setRests(data.restaurants); }
    catch (e) { toast.error(apiError(e)); }
  };

  const order = (r) => {
    localStorage.setItem("bitego_cinema_ctx", JSON.stringify({
      movie: sel.movie.title, cinema: sel.cinema.name, screen: sel.cinema.screen,
      showtime: sel.time, seat, area: sel.cinema.area,
    }));
    localStorage.removeItem("bitego_train_ctx");
    toast.success("Seat delivery selected — order your snacks!");
    nav(`/restaurant/${r.id}`);
  };

  return (
    <div className="min-h-screen bg-surface max-w-[900px] mx-auto pb-24" data-testid="cinema-page">
      <div className="sticky top-0 z-40 bg-white border-b border-border px-3 py-3 flex items-center gap-2">
        <button onClick={() => (rests ? setRests(null) : sel ? setSel(null) : nav(-1))} className="w-9 h-9 flex items-center justify-center tap"><ArrowLeft size={22} /></button>
        <h1 className="font-display font-bold text-lg flex items-center gap-2"><Film size={20} className="text-fuchsia-600" /> Cinema &amp; Events</h1>
      </div>

      <div className="px-4 py-4">
        {!data ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-fuchsia-600" /></div>
        ) : rests ? (
          <div data-testid="cinema-restaurants">
            <div className="bg-white rounded-2xl border border-border p-4 mb-3">
              <div className="flex items-center gap-2 text-sm font-bold text-ink"><Ticket size={16} className="text-fuchsia-600" /> {sel.movie.title}</div>
              <div className="text-xs text-ink-600 mt-1">{sel.cinema.name} · {sel.cinema.screen} · {sel.time} · Seat {seat}</div>
            </div>
            <div className="text-sm font-bold text-ink mb-2 flex items-center gap-2"><Utensils size={16} className="text-fuchsia-600" /> Order snacks to your seat</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {rests.map((r) => (
                <div key={r.id} className="bg-white rounded-2xl border border-border p-3 flex items-center gap-3" data-testid={`cinema-rest-${r.id}`}>
                  <img src={r.image} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0"><div className="font-bold text-sm text-ink truncate">{r.name}</div><div className="text-xs text-ink-600 truncate">{r.cuisine}</div></div>
                  <button onClick={() => order(r)} className="bg-fuchsia-600 text-white font-bold text-sm px-3 py-2 rounded-xl" data-testid={`cinema-order-${r.id}`}>Order</button>
                </div>
              ))}
            </div>
          </div>
        ) : sel ? (
          <div data-testid="cinema-seat">
            <div className="bg-white rounded-2xl border border-border p-4">
              <div className="font-display font-bold text-ink">{sel.movie.title}</div>
              <div className="text-xs text-ink-600">{sel.cinema.name} · {sel.cinema.screen} · {sel.time}</div>
              <div className="mt-3 text-xs font-bold text-ink-600 uppercase">Pick your seat</div>
              <div className="mt-2 grid grid-cols-8 gap-1.5">
                {SEATS.flatMap((row) => [1, 2, 3, 4].map((n) => {
                  const s = `${row}${n}`;
                  return <button key={s} onClick={() => setSeat(s)} className={`text-[11px] py-2 rounded-md font-semibold ${seat === s ? "bg-fuchsia-600 text-white" : "bg-surface text-ink"}`} data-testid={`seat-${s}`}>{s}</button>;
                }))}
              </div>
              <button onClick={confirmSeat} className="w-full bg-fuchsia-600 text-white font-bold py-3 rounded-xl mt-4 tap" data-testid="confirm-seat-btn">Continue to snacks</button>
            </div>
          </div>
        ) : (
          <>
            <div className="bg-gradient-to-br from-fuchsia-700 to-purple-800 text-white rounded-2xl p-4 mb-3">
              <p className="font-display font-bold text-lg">Now showing in {data.city}</p>
              <p className="text-sm text-white/85 mt-0.5">Book your seat vibe &amp; get hot snacks delivered right to it.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {data.movies.map((m) => (
                <div key={m.id} className="bg-white rounded-2xl border border-border overflow-hidden" data-testid={`movie-${m.id}`}>
                  <div className={`h-28 bg-gradient-to-br ${m.grad} flex items-end p-3`}>
                    <div className="text-white"><div className="font-display font-bold">{m.title}</div><div className="text-[11px] opacity-90">{m.genre}</div></div>
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-3 text-xs text-ink-600">
                      <span className="flex items-center gap-1"><Star size={11} className="fill-gold text-gold" /> {m.score}</span>
                      <span>{m.rating}</span><span className="flex items-center gap-1"><Clock size={11} /> {m.duration}</span>
                    </div>
                    {m.cinemas.map((c) => (
                      <div key={c.id} className="mt-2 border-t border-border pt-2">
                        <div className="flex items-center gap-1 text-xs font-semibold text-ink"><MapPin size={11} className="text-fuchsia-600" /> {c.name} · {c.area}</div>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {c.showtimes.map((t) => (
                            <button key={t} onClick={() => chooseTime(m, c, t)} className="text-[11px] font-semibold px-2 py-1 rounded-md border border-border hover:border-fuchsia-600 hover:text-fuchsia-600" data-testid={`showtime-${m.id}-${c.id}-${t.replace(/[^0-9]/g, "")}`}>{t}</button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
